import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { apiDelete, apiGet, apiPatch, apiPost, ApiError } from "../lib/api.js";
import { Panel } from "../components/Panel.js";
import { useAuth } from "../auth/AuthContext.js";

/**
 * Programme management — docs/03-API-SPEC.md §4: "list: any staff (scoped) / create: superadmin",
 * "GET/PATCH/DELETE /:id | programme_admin / superadmin". Creating was superadmin-only from the
 * start; this screen is the first place it was ever reachable from — previously only `POST
 * /programmes` existed, with nothing in the dashboard pointed at it.
 */

interface Programme {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  retentionYears: number;
  isActive: boolean;
}

export const ProgrammesPage = (): JSX.Element => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "" });
  const [error, setError] = useState<string | null>(null);

  const programmesQuery = useQuery({
    queryKey: ["programmes"],
    queryFn: () => apiGet<{ data: Programme[] }>("/programmes"),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["programmes"] });

  const createProgramme = useMutation({
    mutationFn: () => apiPost<Programme>("/programmes", form),
    onSuccess: async () => {
      await invalidate();
      setCreating(false);
      setForm({ name: "", slug: "" });
      setError(null);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not create programme."),
  });

  const toggleActive = useMutation({
    mutationFn: (input: { id: string; isActive: boolean }) =>
      apiPatch(`/programmes/${input.id}`, { isActive: input.isActive }),
    onSuccess: invalidate,
  });

  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteProgramme = useMutation({
    mutationFn: (id: string) => apiDelete(`/programmes/${id}`),
    onSuccess: () => {
      setDeleteError(null);
      return invalidate();
    },
    onError: (err) =>
      setDeleteError(err instanceof ApiError ? err.message : "Could not delete programme."),
  });

  const slugify = (value: string): string =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80);

  const programmes = programmesQuery.data?.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Programmes</h1>
          <p className="text-sm text-muted-foreground">
            Scholarship, Teachers Empowerment, Loco Parentis — the top-level areas every form and
            cycle sits under.
          </p>
        </div>
        {user?.isSuperadmin && (
          <button
            type="button"
            onClick={() => setCreating((v) => !v)}
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary-hover"
          >
            <Plus aria-hidden className="h-4 w-4" />
            New programme
          </button>
        )}
      </div>

      {creating && (
        <Panel title="New programme">
          {error && (
            <p className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </p>
          )}
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              createProgramme.mutate();
            }}
          >
            <label>
              <span className="mb-1 block text-[11px] uppercase text-muted-foreground">Name</span>
              <input
                required
                className="rounded-md border px-2 py-1.5 text-sm"
                value={form.name}
                onChange={(event) =>
                  setForm({ name: event.target.value, slug: slugify(event.target.value) })
                }
              />
            </label>
            <label>
              <span className="mb-1 block text-[11px] uppercase text-muted-foreground">
                URL slug
              </span>
              <input
                required
                className="rounded-md border px-2 py-1.5 font-mono text-sm"
                value={form.slug}
                onChange={(event) => setForm((f) => ({ ...f, slug: slugify(event.target.value) }))}
              />
            </label>
            <button
              type="submit"
              disabled={createProgramme.isPending}
              className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
            >
              {createProgramme.isPending ? "Creating…" : "Create"}
            </button>
          </form>
        </Panel>
      )}

      {deleteError && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {deleteError}
        </p>
      )}

      <div className="space-y-2">
        {programmes.map((programme) => (
          <div
            key={programme.id}
            className={`flex items-center justify-between gap-3 rounded-lg border bg-card p-3 ${
              programme.isActive ? "" : "opacity-50"
            }`}
          >
            <div className="min-w-0">
              <span className="block font-medium">{programme.name}</span>
              <code className="text-xs text-muted-foreground">{programme.slug}</code>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={() =>
                  toggleActive.mutate({ id: programme.id, isActive: !programme.isActive })
                }
                className="rounded-md border px-2 py-1 hover:bg-muted"
              >
                {programme.isActive ? "Active" : "Inactive"}
              </button>
              <button
                type="button"
                title="Delete — a programme with forms under it needs a superadmin"
                onClick={() => {
                  if (
                    window.confirm(
                      `Delete "${programme.name}"? This cannot be undone unless forms are still under it.`,
                    )
                  ) {
                    deleteProgramme.mutate(programme.id);
                  }
                }}
                className="flex items-center gap-1 rounded-md border px-2 py-1 text-destructive hover:bg-destructive/10"
              >
                <Trash2 aria-hidden className="h-3.5 w-3.5" />
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {programmes.length === 0 && !programmesQuery.isLoading && (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No programmes yet.
        </p>
      )}
    </div>
  );
};
